import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Gift, X } from "lucide-react";
import { useSelector } from "react-redux";
import { UserModelData } from "../../types";
import piNetworkAdsManager from "../utils/PiNetworkAdsManager";

const FLASH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const COUNTDOWN_SECONDS = 15;
const BONUS_AMOUNT = 0.5;

const FlashBonusPopup: React.FC = () => {
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [loading, setLoading] = useState(false);
  const [claimed, setClaimed] = useState(false);

  // Check if user is Pro (skip for Pro users)
  const isPro = userData?.user?.isLifetimePro ||
    (userData?.user?.isPro && userData?.user?.proExpiry &&
      new Date(userData.user.proExpiry.seconds * 1000) > new Date());

  useEffect(() => {
    if (isPro) return;
    // Only run on production domain
    if (!window.location.href?.includes("cstry2.web.app")) return;

    const checkAndShow = () => {
      const lastFlash = localStorage.getItem("lastFlashBonusTime");
      const now = Date.now();

      if (!lastFlash || now - Number(lastFlash) >= FLASH_INTERVAL) {
        setIsVisible(true);
        setCountdown(COUNTDOWN_SECONDS);
        setClaimed(false);
        localStorage.setItem("lastFlashBonusTime", now.toString());
      }
    };

    // Check on mount
    checkAndShow();

    // Set interval for future checks
    const interval = setInterval(checkAndShow, FLASH_INTERVAL);

    return () => clearInterval(interval);
  }, [isPro]);

  // Countdown timer
  useEffect(() => {
    if (!isVisible || claimed) return;

    if (countdown <= 0) {
      setIsVisible(false);
      return;
    }

    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [isVisible, countdown, claimed]);

  const handleClaim = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(userData?.user?.id);
      setClaimed(true);
      setTimeout(() => setIsVisible(false), 2000);
    } catch {
      setIsVisible(false);
    } finally {
      setLoading(false);
    }
  }, [loading, userData?.user?.id]);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 20 }}
        className="fixed bottom-28 left-3 right-3 z-[999] max-w-md mx-auto"
      >
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-[2px] shadow-2xl shadow-amber-500/30">
          <div className="relative rounded-[14px] bg-gradient-to-r from-amber-600 via-orange-500 to-red-500 p-4">
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" />

            <div className="relative z-10 flex items-center gap-3">
              {/* Icon */}
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0 backdrop-blur-sm">
                {claimed ? (
                  <span className="text-2xl">✅</span>
                ) : (
                  <Gift size={24} className="text-white" />
                )}
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                {claimed ? (
                  <p className="text-white font-black text-sm">Bonus Claimed! 🎉</p>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-0.5">
                      <Zap size={14} className="text-yellow-200" fill="currentColor" />
                      <span className="text-[10px] font-black text-yellow-200 uppercase tracking-wider">
                        Flash Bonus
                      </span>
                    </div>
                    <p className="text-white font-black text-sm leading-tight">
                      Watch ad for +{BONUS_AMOUNT} ATH FREE!
                    </p>
                  </>
                )}
              </div>

              {/* Action */}
              {!claimed && (
                <div className="flex flex-col items-center gap-1 shrink-0">
                  <button
                    onClick={handleClaim}
                    disabled={loading}
                    className="px-4 py-2.5 rounded-xl bg-white text-orange-600 font-black text-xs active:scale-95 transition-transform shadow-lg"
                  >
                    {loading ? "..." : "CLAIM"}
                  </button>
                  <div className="flex items-center gap-1">
                    <div
                      className="h-1 rounded-full bg-white/30 overflow-hidden"
                      style={{ width: 48 }}
                    >
                      <motion.div
                        className="h-full bg-white"
                        initial={{ width: "100%" }}
                        animate={{ width: "0%" }}
                        transition={{ duration: countdown, ease: "linear" }}
                      />
                    </div>
                    <span className="text-[10px] text-white/70 font-bold">{countdown}s</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FlashBonusPopup;
